import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * tailwind-merge has to be told about our custom scales.
 *
 * Without this it cannot tell a custom font-size from a custom colour: given
 * `text-white text-label`, it classifies BOTH as the text-colour group, keeps the
 * last one, and silently drops `text-white`. The primary button then renders navy
 * text on an orange fill at 3.65:1 — a WCAG failure that looks like a styling
 * quirk. axe caught it; the fix belongs here, once.
 */
const twMerge = extendTailwindMerge({
  extend: {
    // Custom shadow names hit the same ambiguity as the font sizes below:
    // tailwind-merge classifies an unrecognised `shadow-*` against an open-ended
    // colour scale, so `shadow-card` has been resolving as a shadow COLOUR
    // rather than a shadow. Declaring the theme key fixes the pre-existing names
    // and the new ones together.
    theme: {
      // `rounded` (8px) is the base in Button's cva and `rounded-btn` (10px) is
      // the lg-size override. Without this the two would both survive the merge
      // and the winner would come down to Tailwind's output order.
      radius: ['btn'],
      // Container widths (--container-*) back `max-w-*`. Without the theme key,
      // `cn('lg:max-w-main', 'lg:max-w-landing')` keeps both and the winner is
      // decided by the order Tailwind happens to emit them in.
      container: ['page', 'shell', 'main', 'form', 'landing', 'content'],
      shadow: [
        'z1',
        'z4',
        'z8',
        'z12',
        'z16',
        'z20',
        'z24',
        'card',
        'dropdown',
        'dialog',
        'raised',
        'sidebar',
        'panel',
      ],
    },
    classGroups: {
      // Control heights (--height-*). Same reasoning as the shadows: without
      // this, `cn('h-control', 'h-10')` keeps BOTH and the winner is decided by
      // stylesheet order rather than by the caller.
      h: [{ h: ['control', 'field', 'btn-lg', 'btn-md', 'btn-sm', 'header', 'footer'] }],

      // Font sizes from src/styles/theme.css (--text-*). Figma-native names
      // first, then the screen ramp, then the deprecated pre-Figma aliases. All
      // three must be listed for as long as all three resolve to a utility.
      'font-size': [
        {
          text: [
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'subtitle1',
            'subtitle2',
            'body1',
            'body2',
            'caption',
            'overline',
            // screen ramp — see the SCREEN RAMP note in theme.css
            'title-xl',
            'title-l',
            'title-m',
            'title-s',
            'stat',
            'label-m',
            'label-s',
            'label-xs',
            'footnote',
            // deprecated aliases — see the migration note in theme.css
            'label',
            'lead',
            'body',
            'small',
            'micro',
          ],
        },
      ],
    },
  },
})

/**
 * Composes class names and resolves Tailwind conflicts, so a caller's `className`
 * genuinely overrides a component's default rather than depending on stylesheet
 * order.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}
